import React, {useContext} from 'react';
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom'
import PlatformErrorBoundary from "./platform/errorboundary/PlatformErrorBoundary";
import {ComponentsShowcasePage} from "./pages/CompoentsShowcasePage";
import {HomePage} from "./pages/HomePage";
import {ChartShowcasePage} from "./pages/ChartShowcasePage";
import {FanChartShowcasePage} from "./pages/FanChartShowcasePage";

import {BladeProfilesPage} from "./pages/profile/BladeProfilesPage"
import "../node_modules/@fortawesome/fontawesome-free/css/all.min.css";
import {CreateBladeProfilePage} from "./pages/profile/CreateBladeProfilePage";
import {UploadXlsDataPage} from "./pages/xls/UploadXlsDataPage";
import {AdjustableImpellersPage} from "./pages/impeller/AdjustableImpellersPage";
import {SimulationPage} from "pages/SimulationPage";
import {StandardListingPage} from "./pages/standard/StandardListingPage";
import {SimulationExamplesPage} from "./pages/SimulationExamplesPage";
import {MechanicalDesignResultPage} from "./pages/mechanical/MechanicalDesignResultPage";
import MechanicalVerificationPage from "./pages/mechanical/MechanicalVerificationPage";
import {BrowseMechanicalComponentsPage} from "./pages/mechanical/BrowseMechanicalComponentsPage";
import {useParams} from "react-router";
import MechanicalSettingsPage from "./pages/mechanical/MechanicalSettingsPage";
import {StandardLayout} from "./layout/StandardLayout";
import {ImpellerAssemblyPage} from "./pages/mechanical/ImpellerAssemblyPage";
import {EditBladeProfilePage} from "./pages/profile/EditBladeProfilePage";
import {LanguageContext, LanguageContextProvider} from "./platform/i18n/i18n";
import {TranslationRulesPage} from "./pages/i18n/TranslationRulesPage";
import {SetTranslationRulePage} from "./pages/i18n/SetTranslationRulePage";
import {AdjustableImpellerPage} from "./pages/impeller/AdjustableImpellerPage";
import {FanSelectionPage} from "./pages/selection/FanSelectionPage";
import AdminChangePasswordPage from "./platform/account/AdminChangePasswordPage";
import AdminChangeEntitlementsPage from "./platform/account/AdminChangeEntitlementsPage";
import RolesPage from "./platform/authorization/RolesPage";
import AddRolePage from "./platform/authorization/AddRolePage";
import EditRolePage from "./platform/authorization/EditRolePage";
import {MainLayoutWebFormPage} from "./platform/webform/WebFormPages";
import BrowseUserAccountPage from "./platform/account/BrowseUserAccountPage";
import ActivateUserAccountsPage from "./platform/account/ActivateUserAccountsPage";
import DeleteMyAccountPage from "./platform/account/DeleteMyAccountPage";
import DeletedAccountsPage from "./platform/account/DeletedAccountsPage";
import AuthenticationContextProvider, {AuthenticationContext} from "./platform/authentication/AuthenticationContextProvider";
import {OptiselSignInPage} from "./components/OptiselSignInPage";
import {RegisterUserPage} from "./pages/registration/RegisterUserPage";
import {RegistrationConfirmPage} from "./pages/registration/RegistrationConfirmPage";
import {RequestPasswordResetPage} from "./pages/passwordreset/RequestPasswordResetPage";
import {SetNewPasswordPage} from "./pages/passwordreset/SetNewPasswordPage";
import {NoAccessLandingPage} from "./pages/account/NoAccessLandingPage";
import {TestEmailPage} from "./pages/admin/TestEmailPage";
import {useCurrentUserPrivileges} from "./platform/hooks/PlatformHooks";
import WorkspaceContextProvider from "./platform/workspace/WorkspaceContextProvider";
import {OptiselEventHistoryPage} from "./pages/optiselevent/OptiselEventHistoryPage";
import {ShowOptiselEventPage} from "./pages/optiselevent/ShowOptiselEventPage";
import {HistoricalUploadsPage} from "./pages/historicalupload/HistoricalUploadsPage";
import {DatasheetPage} from "./pages/datasheet/DatasheetPage";
import {BrowseIssuesPage} from "./pages/issues/BrowseIssuesPage";
import {ShowIssuePage} from "./pages/issues/ShowIssuePage";
import {CreateIssuePage} from "./pages/issues/CreateIssuePage";
import {EditIssuePage} from "./pages/issues/EditIssuePage";
import {HotIssueContextProvider} from "./pages/issues/hotssue/HotIssueContextProvider";
import {EditStandardPage} from "./pages/standard/EditStandardPage";
import {UserPreferencesProvider} from "./contexts/UserPreferencesContext";
import {BrowseTechDrawsPage} from "./pages/techdraw/BrowseTechDrawsPage";
import {EditTechDrawPage} from "./pages/techdraw/EditTechDrawPage";
import {TechDrawExportImportPage} from "./pages/techdraw/TechDrawExportImportPage";
import {MultiFanNoisePage} from "./pages/multifannoise/MultiFanNoisePage";
import {Protected} from "./layout/Protected";


function App() {

    function ApplicationRouting() {
        // Note this is used only to trigger rerender if context value changes.
        useContext(LanguageContext);


        const authenticationContext = useContext(AuthenticationContext);
        const authenticated = authenticationContext.isAuthenticated();
        const authenticationStatusKnown = authenticationContext.isAuthenticationStatusKnown();
        const privileges = useCurrentUserPrivileges();

        const BrowseMechanicalDataPageProxy = (props) => {
            // A proxy for truly remounting of whole mechanical data browsing page on router path change
            const {datatype} = useParams();
            return <BrowseMechanicalComponentsPage key={datatype} {...props} />;
        }


        if (!authenticationStatusKnown) {
            return <div className={"text-center mt-4"}>...</div>;
        } else {
            if (!authenticated) {
                console.log("Not authenticated")
                return <Routes>
                    <Route exact path='/sign-in' element={<OptiselSignInPage/>}/>
                    <Route exact path='/register' element={<RegisterUserPage/>}/>
                    <Route exact path='/registration/confirm/:code' element={<RegistrationConfirmPage/>}/>
                    <Route exact path='/reset-password' element={<RequestPasswordResetPage/>}/>
                    <Route exact path='/reset-password/:code' element={<SetNewPasswordPage/>}/>
                    <Route path={"*"} element={<Navigate to="/sign-in"/>}/>
                </Routes>
            }
        }


        if (privileges === "loading") {
            return <div className={"text-center mt-4"}>...</div>;
        }
        if (privileges.length === 0) {
            return <NoAccessLandingPage/>;
        }

        return <StandardLayout includeContentCard={false}>
            <Routes>
                <Route exact path='/' element={<HomePage/>}/>
                <Route exact path='/components-showcase' element={<ComponentsShowcasePage/>}/>
                <Route exact path='/chart-showcase' element={<ChartShowcasePage/>}/>
                <Route exact path='/fan-chart-showcase' element={<FanChartShowcasePage/>}/>
                <Route exact path='/blade-profiles' element={<BladeProfilesPage/>}/>
                <Route exact path='/adjustable-impellers' element={<AdjustableImpellersPage/>}/>
                <Route exact path='/adjustable-impeller/:id' element={<AdjustableImpellerPage/>}/>
                <Route exact path='/create-blade-profile' element={<CreateBladeProfilePage/>}/>
                <Route exact path='/edit-blade-profile/:id' element={<EditBladeProfilePage/>}/>
                <Route exact path='/simulation' element={<SimulationPage/>}/>
                <Route exact path='/datasheet' element={<DatasheetPage/>}/>
                <Route exact path='/standard-listing' element={<StandardListingPage/>}/>
                <Route exact path='/standard-listing/edit/:id/:entryIdx' element={<EditStandardPage/>}/>
                <Route exact path='/upload-xls-data' element={<UploadXlsDataPage/>}/>
                <Route exact path='/historical-uploads' element={<HistoricalUploadsPage/>}/>
                <Route exact path='/simulation-examples' element={<SimulationExamplesPage/>}/>
                <Route exact path='/multi-fan-noise' element={<MultiFanNoisePage/>}/>
                <Route exact path='/mechanical-design/:id' element={<MechanicalDesignResultPage/>}/>
                <Route exact path='/selection' element={<FanSelectionPage/>}/>
                <Route exact path='/event-history' element={<Protected privilege={"PERM_HISTORY_AND_ISSUES"}><OptiselEventHistoryPage/></Protected>}/>
                <Route exact path='/optisel-event/:id' element={<Protected privilege={"PERM_HISTORY_AND_ISSUES"}><ShowOptiselEventPage/></Protected>}/>
                <Route exact path='/mechanical-verification' element={<MechanicalVerificationPage/>}/>
                {/* eslint-disable-next-line no-restricted-globals */}
                <Route exact path='/mechanical-component/:datatype' element={<BrowseMechanicalDataPageProxy/>}/>
                <Route exact path='/mechanical-settings' element={<MechanicalSettingsPage/>}/>
                <Route exact path='/impeller-assembly' element={<ImpellerAssemblyPage/>}/>
                <Route exact path='/translation-rules' element={<TranslationRulesPage/>}/>
                <Route exact path='/set-translation-rule' element={<SetTranslationRulePage/>}/>
                <Route exact path='/browse-issues' element={<Protected privilege={"PERM_HISTORY_AND_ISSUES"}><BrowseIssuesPage/></Protected>}/>

                <Route exact path='/browse-tech-draws' element={<BrowseTechDrawsPage/>}/>
                <Route exact path='/edit-tech-draw/:id' element={<EditTechDrawPage/>}/>
                <Route exact path='/tech-draw-export-import' element={<TechDrawExportImportPage/>}/>

                <Route exact path='/show-issue/:id' element={<Protected privilege={"PERM_HISTORY_AND_ISSUES"}><ShowIssuePage/></Protected>}/>
                <Route exact path="/create-issue" element={<Protected privilege={"PERM_HISTORY_AND_ISSUES_MANAGEMENT"}><CreateIssuePage/></Protected>}/>
                <Route exact path="/edit-issue/:id" element={<Protected privilege={"PERM_HISTORY_AND_ISSUES_MANAGEMENT"}><EditIssuePage/></Protected>}/>


                <Route exact path='/create-account' element={<MainLayoutWebFormPage
                    submitLabel={"Utwórz"}
                    title={"Utwórz konto użytkownika"}
                    webFormContext={"create-account"}
                    successHandler={() => {
                        alert("Konto użytkownika zostało utworzone");
                        window.location = '/accounts';
                    }}/>}/>
                <Route exact path='/accounts/change-password/:id' element={<AdminChangePasswordPage/>}/>
                <Route exact path='/change-password' element={<MainLayoutWebFormPage
                    title={"Zmień hasło"}
                    submitLabel={"Ustaw nowe hasło"}
                    webFormContext={"self-service-change-password"}
                    successHandler={() => {
                        alert("Hasło zostało zmienione");
                        window.location = '/';
                    }}/>}/>
                <Route exact path='/accounts/change-entitlements/:id' element={<AdminChangeEntitlementsPage/>}/>
                <Route exact path='/roles' element={<RolesPage/>}/>
                <Route exact path='/roles/add' element={<AddRolePage/>}/>
                <Route exact path='/roles/edit/:id' element={<EditRolePage/>}/>
                <Route exact path='/accounts' element={<BrowseUserAccountPage/>}/>
                <Route exact path='/accounts/activate-users' element={<ActivateUserAccountsPage/>}/>
                <Route exact path='/accounts/deleted' element={<Protected privilege={"PERM_USER_ACCESS_MANAGEMENT"}><DeletedAccountsPage/></Protected>}/>
                <Route exact path='/delete-my-account' element={<Protected privilege={"PERM_AUTHENTICATED_USER"}><DeleteMyAccountPage/></Protected>}/>
                <Route exact path='/admin/test-email' element={<TestEmailPage/>}/>


            </Routes>
        </StandardLayout>
    }

    // return <BrowserRouter>
    //     <ApplicationRouting/>
    // </BrowserRouter>;


    return <PlatformErrorBoundary>
        <AuthenticationContextProvider>
            <UserPreferencesProvider>
                <WorkspaceContextProvider>
                    <LanguageContextProvider>
                        <HotIssueContextProvider>
                            <BrowserRouter>
                                <ApplicationRouting/>
                            </BrowserRouter>
                        </HotIssueContextProvider>
                    </LanguageContextProvider>
                </WorkspaceContextProvider>
            </UserPreferencesProvider>
        </AuthenticationContextProvider>
    </PlatformErrorBoundary>;

}

export default App;
